import React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertCircle,
  Building2,
  CalendarDays,
  CheckCircle2,
  Equal,
  IndianRupee,
  Info,
  Loader2,
  Receipt,
  SlidersHorizontal,
  TrendingDown,
  TrendingUp,
  User,
  Users,
} from "lucide-react";
import { money, capCycle } from "./billingFormat";
import { FigureStrip, Figure } from "./FigureStrip";

/** "2026-08-24" → "24 Aug 2026", parsed as a LOCAL date. */
const prettyDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = String(iso).split("-").map(Number);
  // new Date("2026-08-24") is parsed as UTC midnight, which renders as the
  // previous day west of Greenwich. Build it from parts instead.
  if (!y || !m || !d) return String(iso);
  return new Date(y, m - 1, d).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const cycleWord = (c) => (c === "annual" ? "annual" : "monthly");
const cycleShort = (c) => (c === "annual" ? "yr" : "mo");
const cyclePeriod = (c) => (c === "annual" ? "year" : "month");

/**
 * One line naming everything that differs, so the headline figure never has to
 * be reverse-engineered from the form below it.
 */
function describeChange(p, renew, startDate) {
  const parts = [];
  if (renew) {
    parts.push(`New paid term${startDate ? ` from ${prettyDate(startDate)}` : ""}`);
  }
  if (p.switchingPlan) {
    parts.push(`${p.sub.plan_name || "current plan"} → ${p.plan?.plan_name || "new plan"}`);
  }
  if (p.cycleChanging) {
    parts.push(`${cycleWord(p.sub.billing_cycle)} → ${cycleWord(p.cycle)} billing`);
  }
  if (p.seatsChanging) {
    const diff = p.seats - Number(p.sub.number_of_employees ?? 0);
    parts.push(
      diff > 0
        ? `${diff} additional licensed seat${diff === 1 ? "" : "s"}`
        : `${-diff} fewer seat${diff === -1 ? "" : "s"}`
    );
  }
  if (p.revertingToList) parts.push("back to list price");
  else if (p.rateChanging) parts.push(`negotiated rate of ${money(p.perUser)} / user`);
  if (!parts.length) return null;
  return `${parts.join(" · ")} (${p.seats} seat${p.seats === 1 ? "" : "s"} total)`;
}

/**
 * The direct-provisioning dialog for an offline-billed company.
 *
 * Pure presentation — every value and setter comes from useProvisionCompany, so
 * the two can be mounted on any page that has the getCompanyById payload.
 *
 * Reading order is deliberately outcome-first: what they are on now → what this
 * will cost them → the controls that change it. The money tile used to sit
 * inside the form grid, below the fold on a short viewport, which put the one
 * figure being decided last in the page.
 *
 * @param company    the getCompanyById payload (name, statistics, subscription)
 * @param provision  the useProvisionCompany() return value, spread as-is
 */
export default function ProvisionSubscriptionDialog({ company, provision }) {
  const {
    availablePlans,
    provisionOpen, setProvisionOpen,
    provisionSeats, setProvisionSeats,
    provisionPlanId, setProvisionPlanId,
    provisionCycle, setProvisionCycle,
    provisionCustom, setProvisionCustom,
    provisionPrice, setProvisionPrice,
    provisionNote, setProvisionNote,
    provisioning,
    provisionRenew,
    provisionStartDate, setProvisionStartDate,
    provisionPreview,
    quote,
    quoteLoading,
    quoteError,
    quoteExpected,
    submitProvision,
  } = provision;

  const p = provisionPreview;

  /**
   * What the company will actually be invoiced, priced BY THE SERVER.
   *
   * Everything else on this card is derived locally from the subscription's
   * whole-cycle amounts, which is fine for "what will it bill per year" but
   * wrong for "what do we charge now": a mid-term change bills only the months
   * it buys. Deriving that here would mean a second copy of the proration
   * rules, and the first thing a second copy does is disagree with the invoice.
   */
  const charge = quote?.charge || null;
  const chargeAmount = charge?.will_invoice ? charge.gross_amount : null;
  const isProratedCharge = charge?.amount_basis === "prorated";
  const chargeWindow =
    isProratedCharge && charge.remaining_months != null && charge.total_cycle_months != null
      ? `${charge.remaining_months} of ${charge.total_cycle_months} month${charge.total_cycle_months === 1 ? "" : "s"}`
      : null;

  /**
   * Close enough to the month boundary that waiting is worth mentioning.
   *
   * Five days, and only when more than one month remains — in the final month
   * there is nothing to wait for, and telling someone to delay past the end of
   * their own term would be nonsense.
   */
  const nearBoundary =
    isProratedCharge &&
    charge.days_to_next_boundary != null &&
    charge.days_to_next_boundary <= 5 &&
    charge.remaining_months > 1 &&
    charge.saving_if_waited != null;
  const boundaryDate = charge?.next_month_boundary
    ? new Date(charge.next_month_boundary).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
      })
    : null;

  /**
   * Why the submit button is greyed out, in words.
   *
   * The button used to be disabled by a nine-clause boolean with no matching
   * on-screen message for most of them — a user could hit a dead end (no plan
   * picked, empty custom price) with nothing to read. The clause list is
   * UNCHANGED; it is only ordered and given text, so nothing newly passes or
   * newly fails.
   */
  const blockedReason =
    !provisionPlanId
      ? "Pick a plan to continue."
      : !p.validSeats
        ? "Enter a valid seat count."
        : p.overCap
          ? `Seats exceed this plan's cap of ${p.cap}.`
          : p.belowFloor
            ? p.floorReason === "licensed"
              ? `Seats can't go below the ${p.currentSeats} already licensed for this term.`
              : `Seats can't go below the ${p.employeeCount} employees on the books.`
            : p.isDowngrade && !provisionRenew
              ? "Mid-term downgrades aren't allowed — downgrade at renewal instead."
              : provisionRenew && !provisionStartDate
                ? "Pick the date the new term starts."
                : provisionCustom && provisionPrice === ""
                  ? "Enter the negotiated per-user rate."
                  : /**
                     * A clause for `isPriceOnly && !provisionCustom &&
                     * !revertingToList` used to sit here. It can never be true:
                     * isPriceOnly requires rateChanging, and with custom
                     * unticked the only way to get rateChanging is
                     * revertingToList — which the clause then excludes.
                     * Verified by enumerating the dialog's whole input space
                     * against the real hook (128 states, never once reached).
                     * The "nothing changed" case below is what actually fires.
                     */
                    p.nothingChanges && !provisionRenew
                    ? "Nothing to apply — change a value first."
                    : null;

  /**
   * What the admin actually invoices.
   *
   * A RENEWAL bills the whole term — it is a fresh commitment, so the headline
   * is the full gross amount. A mid-term UPDATE bills only the difference.
   * Collapsing the two into one "delta" figure would understate a renewal
   * invoice by the entire value of the term.
   */
  const hasDelta = p.delta != null && p.delta !== 0;
  const up = hasDelta && p.delta > 0;

  /**
   * Is a price still on its way for the values currently on screen?
   *
   * This gates the headline entirely. The locally-derived figure is a
   * WHOLE-CYCLE amount, so showing it while the request is in flight flashes a
   * number up to 12x the real one and then corrects itself — which reads as
   * the price changing under you. A skeleton says "not yet" honestly; a wrong
   * number does not.
   */
  const awaitingQuote = quoteExpected && !quoteError && (quoteLoading || !quote);

  /**
   * The amount invoiced, and ONLY ever the server's answer.
   *
   * Nothing derived reaches this. A renewal happens to be billed the full gross
   * amount, which the client could compute correctly — but making that the one
   * exception means a reader has to know which branches are trustworthy, and
   * the mid-term branch already proved that assumption fails silently.
   */
  // Always the server's figure, and a server-priced charge is always money
  // owed to us — so it is never rendered with a +/− sign.
  const headline = chargeAmount;

  /**
   * A change can be real and still bill nothing — dropping the rate, or
   * re-provisioning at the same seat count. The card then has no charge to
   * headline, so it shows what the company will be billed PER CYCLE from now
   * on instead, and says plainly that no invoice follows.
   *
   * Without this the "Billing will decrease by" label from the derived tone
   * would sit above a recurring total, describing it as a reduction.
   */
  const noChargeFromQuote = quote != null && charge != null && !charge.will_invoice;

  /**
   * Percent moved, on the SAME basis as the figures beside it — gross vs gross.
   * The old-amount cell above is `oldGrossAmount` for the same reason: a
   * GST-inclusive new total compared against an ex-GST current amount reads as
   * one sentence and disagrees with itself.
   */
  const pct =
    !provisionRenew && hasDelta && p.oldGrossAmount
      ? (p.delta / p.oldGrossAmount) * 100
      : null;

  // Teal is revenue going UP in the vendor's own console; amber is revenue
  // coming down. Neutral when the figure is unchanged.
  const tone =
    provisionRenew || up
      ? {
          card: "border-primary/30 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent",
          orb: "bg-gradient-to-br from-primary/30 to-primary/10 ring-1 ring-primary/40 text-primary",
          text: "text-primary",
          Icon: TrendingUp,
          label: "You will bill this company",
        }
      : hasDelta
        ? {
            card: "border-amber-500/30 bg-gradient-to-br from-amber-500/10 via-amber-500/5 to-transparent",
            orb: "bg-gradient-to-br from-amber-500/30 to-amber-500/10 ring-1 ring-amber-500/40 text-amber-600 dark:text-amber-400",
            text: "text-amber-600 dark:text-amber-400",
            Icon: TrendingDown,
            label: "Billing will decrease by",
          }
        : {
            card: "border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50",
            orb: "bg-slate-200/60 dark:bg-slate-700/60 text-slate-500 dark:text-slate-400",
            text: "text-slate-900 dark:text-foreground",
            Icon: Equal,
            label: "No change to the amount",
          };

  /**
   * The caption above the figure.
   *
   * Declared after `tone` because it falls back to it. While a price is in
   * flight there is nothing to caption yet, and a change that bills nothing is
   * captioned as a recurring amount rather than as a charge — otherwise the
   * derived "Billing will decrease by" would sit above a per-cycle total and
   * describe it as a reduction.
   */
  const displayLabel = awaitingQuote
    ? "Pricing this change…"
    : noChargeFromQuote
      ? `New amount per ${cyclePeriod(p.cycle)}`
      : tone.label;

  const summary = describeChange(p, provisionRenew, provisionStartDate);

  return (
      <Dialog open={provisionOpen} onOpenChange={(v) => !provisioning && setProvisionOpen(v)}>
        {/* max-h + overflow so a short viewport scrolls the dialog rather than
            clipping the footer buttons out of reach. */}
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto dark:bg-slate-900">
          <DialogHeader className="space-y-2">
            <DialogTitle className="flex items-center gap-3 text-2xl">
              <span className="h-10 w-10 shrink-0 rounded-xl bg-primary/10 border border-primary/25 flex items-center justify-center">
                {provisionRenew
                  ? <CheckCircle2 className="h-5 w-5 text-primary" />
                  : <SlidersHorizontal className="h-5 w-5 text-primary" />}
              </span>
              {provisionRenew ? "Renew subscription" : "Update subscription"}
            </DialogTitle>
            <DialogDescription>
              {provisionRenew ? "Starts a fresh paid term for " : "Applied immediately for "}
              <span className="font-semibold text-slate-700 dark:text-slate-200">
                {company?.company?.company_name}
              </span>
              . No payment is collected — bill this company separately.
            </DialogDescription>
          </DialogHeader>

          {/* Where they are now. The whole dialog is a before→after, so state
              the "before" instead of making the user recall it. */}
          <FigureStrip cols={4} cellClass="bg-slate-50 dark:bg-slate-800/60">
            <Figure
              icon={Building2}
              value={p.sub.plan_name || "—"}
              label="Plan"
            />
            <Figure
              icon={CalendarDays}
              value={capCycle(p.sub.billing_cycle) || "—"}
              label="Billing cycle"
            />
            <Figure
              icon={Users}
              value={`${p.sub.number_of_employees ?? 0} seats`}
              label="Currently licensed"
            />
            <Figure
              icon={IndianRupee}
              /* Derived from rate × seats, never the denormalised `sub.amount`
                 — that field only refreshes on a model `.save()` and has drifted
                 by thousands before. Gross, to match the new total below it. */
              value={money(p.oldGrossAmount)}
              label={`Current ${cycleWord(p.sub.billing_cycle)} amount · incl. GST`}
              sub={
                p.hadCustom ? (
                  <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-violet-100 dark:bg-violet-900/30 px-2 py-0.5 text-[10px] font-semibold text-violet-700 dark:text-violet-300">
                    <Receipt className="h-3 w-3" /> custom rate
                  </span>
                ) : null
              }
            />
          </FigureStrip>

          {/* The outcome, before the controls that produce it. */}
          {p.newAmount != null ? (
            <div className={`rounded-2xl border p-4 sm:p-5 space-y-4 ${tone.card}`}>
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <span className={`h-16 w-16 shrink-0 rounded-full flex items-center justify-center ${tone.orb}`}>
                  <tone.Icon className="h-7 w-7" strokeWidth={2.25} />
                </span>

                <div className="min-w-0 flex-1">
                  <p className={`text-xs font-bold uppercase tracking-wider ${tone.text}`}>
                    {displayLabel}
                  </p>
                  {awaitingQuote ? (
                    /* A shaped placeholder rather than a figure — see
                       `awaitingQuote`: anything derivable here is a whole-cycle
                       amount and would be wrong for a mid-term change. */
                    <div className="mt-1 flex items-center gap-2.5">
                      <span className="h-9 sm:h-10 w-40 animate-pulse rounded-lg bg-slate-200 dark:bg-slate-700" />
                      <span className="text-xs font-medium text-slate-400">
                        pricing…
                      </span>
                    </div>
                  ) : (
                    <p className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-foreground leading-tight mt-1">
                      {money(headline != null ? headline : p.grossAmount)}
                    </p>
                  )}

                  {/*
                    A prorated charge covers only part of the term, and the
                    figure cannot be derived from rate x seats — say which
                    window it covers, or it reads as though it were wrong.
                  */}
                  {/* All of these describe a charge, so none of them may appear
                      before that charge does. */}
                  {!awaitingQuote && chargeWindow && (
                    <p className="text-xs font-semibold text-amber-700 dark:text-amber-300 mt-1">
                      Prorated — {chargeWindow} left on the current term
                    </p>
                  )}

                  {/*
                    The month in progress is billed in FULL, so provisioning the
                    day before it ends charges a whole month for a day of it.
                    Say so while there is still time to wait, with the date and
                    the saving — a warning without either is just noise.
                  */}
                  {!awaitingQuote && nearBoundary && (
                    <p className="mt-1.5 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
                      <span className="font-semibold">
                        This billing month ends in {charge.days_to_next_boundary}{" "}
                        {charge.days_to_next_boundary === 1 ? "day" : "days"}
                        {boundaryDate ? ` (${boundaryDate})` : ""}.
                      </span>{" "}
                      Part-months are charged in full. Applying this on or after
                      that date would bill {money(charge.saving_if_waited)} less.
                    </p>
                  )}
                  {!awaitingQuote && noChargeFromQuote && (
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                      Nothing to invoice for this change — only what recurs changes.
                    </p>
                  )}
                  {quoteError && (
                    <p className="text-xs font-semibold text-red-600 dark:text-red-400 mt-1">
                      {quoteError}
                    </p>
                  )}

                  {summary && (
                    <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">{summary}</p>
                  )}
                </div>

                <div className="sm:text-right shrink-0 sm:border-l sm:pl-5 border-slate-200 dark:border-slate-700/70">
                  {provisionRenew ? (
                    <>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Term starts</p>
                      <p className="text-xl font-bold text-slate-900 dark:text-foreground mt-0.5">
                        {provisionStartDate ? prettyDate(provisionStartDate) : "—"}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                        Runs one {cyclePeriod(p.cycle)} from this date
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        New {cycleWord(p.cycle)} amount
                      </p>
                      <p className="text-2xl font-bold text-slate-900 dark:text-foreground mt-0.5">
                        {money(p.grossAmount)}
                        <span className="text-xs font-normal text-slate-500 dark:text-slate-400">
                          {" / "}{cycleShort(p.cycle)}
                        </span>
                      </p>
                      {pct != null && (
                        <span className={`mt-1.5 inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${
                          up
                            ? "border-primary/30 bg-primary/10 text-primary"
                            : "border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-400"
                        }`}>
                          {up ? "+" : "−"}{Math.abs(pct).toFixed(2)}%{" "}
                          {up ? "increase" : "decrease"}
                        </span>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* How the total is built, so the headline can be checked rather
                  than trusted. */}
              <FigureStrip cols={4}>
                <Figure
                  icon={User}
                  value={money(p.perUser)}
                  label={`Per user / ${cyclePeriod(p.cycle)}`}
                  sub={
                    p.usingCustom ? (
                      <span className="text-[10px] font-semibold text-violet-600 dark:text-violet-400">
                        custom rate
                      </span>
                    ) : null
                  }
                />
                <Figure
                  value={money(p.newAmount)}
                  label={`Base × ${p.seats} seats`}
                />
                <Figure
                  value={money(p.taxTotal)}
                  /* Maharashtra splits the same 18% into CGST + SGST 9/9;
                     everywhere else it is a single IGST. Mirrors
                     calculateSubscriptionGST on the server. */
                  label={p.isMaharashtra ? "CGST + SGST (18%)" : "IGST (18%)"}
                  sub={
                    p.isMaharashtra ? (
                      <span className="text-[10px] text-slate-400 dark:text-slate-500">
                        {/* p.cgst, not taxTotal/2 — the two halves are each
                            rounded from the base at 9%, so halving the total
                            can differ by a paisa from what is billed. */}
                        {money(p.cgst)} each
                      </span>
                    ) : null
                  }
                />
                <Figure
                  icon={Equal}
                  value={money(p.grossAmount)}
                  label="New total"
                />
              </FigureStrip>

              {/* Consequences that outlive this dialog. */}
              <div className="space-y-2">
                <div className="flex items-start gap-2.5 rounded-xl border border-blue-500/30 bg-blue-500/5 px-3.5 py-3 text-sm text-slate-700 dark:text-slate-300">
                  <Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-500 dark:text-blue-400" />
                  <div>
                    <p>This change takes effect immediately.</p>
                    <p className="text-slate-500 dark:text-slate-400">
                      An invoice or payment entry will be created separately by you.
                    </p>
                  </div>
                </div>

                {(p.cycleChanging || p.customWillBeLost) && (
                  <div className="flex items-start gap-2.5 rounded-xl border border-amber-500/40 bg-amber-500/5 px-3.5 py-3 text-sm text-amber-700 dark:text-amber-300">
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                    <div className="space-y-1">
                      {p.cycleChanging && (
                        <p>
                          Next billing date moves to one {cyclePeriod(p.cycle)} from today.
                        </p>
                      )}
                      {p.customWillBeLost && (
                        <p>
                          Replaces the negotiated {money(p.currentRate)} / user with{" "}
                          {p.plan?.plan_name}'s list price.
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-slate-300 dark:border-slate-700 px-4 py-6 text-center text-sm text-slate-500 dark:text-slate-400">
              Pick a plan and a seat count to see what this company will be billed.
            </div>
          )}

          {/* Renewal: when the new term begins. Only shown when renewing — a
              plain seat/plan tweak never re-dates a running term. */}
          {provisionRenew && (
            <div className="space-y-1.5 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3.5">
              <Label htmlFor="provision-start-date" className="flex items-center gap-1.5">
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                New term starts on <span className="text-red-500">*</span>
              </Label>
              <input
                id="provision-start-date"
                type="date"
                required
                value={provisionStartDate}
                onChange={(e) => setProvisionStartDate(e.target.value)}
                className="w-full rounded-md border border-slate-300 dark:border-slate-700 px-3 py-2 bg-background text-sm"
              />
              <p className="text-xs text-slate-500 dark:text-slate-400">
                The paid term runs one {cyclePeriod(p.cycle)} from this date. Backdate it to
                when payment was actually received.
              </p>
            </div>
          )}

          {/* Two columns so the whole form fits without scrolling. Collapses to
              one on narrow screens. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-4">
            <div className="space-y-1.5">
              <Label>Plan</Label>
              <Select value={provisionPlanId} onValueChange={setProvisionPlanId}>
                <SelectTrigger><SelectValue placeholder="Select a plan" /></SelectTrigger>
                <SelectContent>
                  {availablePlans.map((plan) => {
                    // While the term is running (not a renewal), a lower-tier
                    // plan is a downgrade — disabled here, blocked on the server.
                    const isLower =
                      !provisionRenew &&
                      p.currentPlanOrder != null &&
                      plan.display_order != null &&
                      Number(plan.display_order) < Number(p.currentPlanOrder);
                    return (
                      <SelectItem key={plan.plan_id} value={plan.plan_id} disabled={isLower}>
                        {plan.plan_name}
                        {plan.employee_limit === -1
                          ? " · unlimited"
                          : plan.employee_limit != null
                            ? ` · up to ${plan.employee_limit}`
                            : ""}
                        {/* Makes it obvious the field is pre-filled with what
                            the company is already on, not an empty default.
                            Reads the preview's `sub`, which falls back to the
                            latest subscription — on an ENDED term
                            `company.subscription` is null and the tag never
                            showed, which is exactly when a super-admin is here. */}
                        {plan.plan_id === p.sub.plan_id ? " · current" : ""}
                        {isLower ? " · downgrade (at renewal)" : ""}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              {p.isDowngrade && !provisionRenew && (
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  This company's plan is on a running term — you can only move it
                  to a higher plan mid-term. Downgrade at renewal instead.
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label>Billing cycle</Label>
                <div className="grid grid-cols-2 gap-2">
                  {["monthly", "annual"].map((c) => {
                    // An annual term is PREPAID. Switching to monthly mid-cycle
                    // re-anchors the next billing date a month out, discarding
                    // the remainder of a year the company already paid for.
                    const blocked =
                      c === "monthly" && company?.subscription?.can_switch_to_monthly === false;
                    return (
                      <button
                        key={c}
                        type="button"
                        disabled={blocked}
                        aria-pressed={provisionCycle === c}
                        title={blocked ? "Annual term is prepaid — switch at renewal" : undefined}
                        onClick={() => !blocked && setProvisionCycle(c)}
                        className={`px-3 py-2.5 rounded-lg text-sm font-medium capitalize transition border ${
                          blocked
                            ? "bg-slate-100 dark:bg-slate-800/50 text-slate-400 dark:text-slate-600 border-slate-200 dark:border-slate-700 cursor-not-allowed"
                            : provisionCycle === c
                              ? "bg-primary text-primary-foreground border-primary shadow-sm"
                              : "bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700"
                        }`}
                      >
                        {c}
                      </button>
                    );
                  })}
                </div>
                {company?.subscription?.can_switch_to_monthly === false && (
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Annual term is prepaid — monthly can only be set at renewal.
                  </p>
                )}
              </div>

            <div className="space-y-1.5">
              <Label htmlFor="provision-seats">Licensed seats</Label>
              {/* Steppers: seat counts move by small increments, so make the
                  common action a click rather than a keyboard edit. */}
              <div className="flex items-center gap-2">
                {/* Clamped to the floor so the stepper can't reach an invalid
                    value at all, rather than letting the user land on one and
                    then telling them off. */}
                <button
                  type="button"
                  disabled={Number(provisionSeats) <= p.seatFloor}
                  onClick={() =>
                    setProvisionSeats((s) => Math.max(p.seatFloor, Number(s) - 1))
                  }
                  className="h-10 w-10 shrink-0 rounded-lg border border-slate-200 dark:border-slate-700 text-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                  aria-label="Decrease seats"
                >
                  −
                </button>
                <Input
                  id="provision-seats"
                  type="number"
                  min={p.seatFloor}
                  className="text-center h-10 text-base font-semibold"
                  value={provisionSeats}
                  onChange={(e) => setProvisionSeats(e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => setProvisionSeats((s) => Number(s) + 1)}
                  className="h-10 w-10 shrink-0 rounded-lg border border-slate-200 dark:border-slate-700 text-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
                  aria-label="Increase seats"
                >
                  +
                </button>
              </div>
              {/* This is a total, not a delta — the backend sets the seat count
                  to exactly this number, matching how request approval works.
                  The floor is the ROSTER when seats can be reduced, and the
                  seats ALREADY PAID FOR while the term is running; naming the
                  wrong one of those names the wrong number. */}
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Minimum {p.seatFloor} seats
                {" ("}
                {p.floorReason === "licensed"
                  ? `${p.currentSeats} already licensed`
                  : `${p.employeeCount} employees on the books`}
                {")"}
              </p>
              {(p.overCap || p.belowFloor) && (
                <div className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-red-700 dark:border-red-800/70 dark:bg-red-950/40 dark:text-red-300">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <div className="space-y-0.5 text-xs font-medium">
                    {p.overCap && (
                      <p>
                        Exceeds the plan cap of {p.cap} seats. Upgrade the plan to license more.
                      </p>
                    )}
                    {p.belowFloor && (
                      p.floorReason === "licensed" ? (
                        <p>
                          Below the {p.currentSeats} seats already licensed for this term.
                          Seats can only be reduced at renewal.
                        </p>
                      ) : (
                        <p>Below the {p.employeeCount} employees already on the books.</p>
                      )
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Custom / negotiated pricing */}
            <div
              className={`space-y-2 rounded-lg border p-3 self-start transition ${
                provisionCustom
                  ? "border-violet-300 dark:border-violet-800 bg-violet-50/50 dark:bg-violet-950/20"
                  : "border-slate-200 dark:border-slate-700"
              }`}
            >
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={provisionCustom}
                  onCheckedChange={(v) => setProvisionCustom(!!v)}
                />
                <span className="text-sm font-medium">
                  {p.isPriceOnly
                    ? `Negotiated rate · per user · ${capCycle(p.cycle)}`
                    : "Custom pricing"}
                </span>
              </label>

              {/* Unticking is a real instruction, not just "leave it alone" —
                  say what it will do and what the company will pay. */}
              {p.revertingToList && (
                <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-800/70 dark:bg-amber-950/30 dark:text-amber-300">
                  Ends this company's negotiated rate. They move back to the{" "}
                  {p.plan?.plan_name || "plan"} list price of{" "}
                  <span className="font-semibold">
                    {p.planListRate != null ? money(p.planListRate) : "—"}
                  </span>
                  /user, from {money(p.currentRate)}/user today.
                </div>
              )}

              {provisionCustom ? (
                <div className="space-y-1.5">
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none">
                      INR
                    </span>
                    <Input
                      id="provision-price"
                      type="number"
                      min={0}
                      step="0.01"
                      className="pl-11"
                      value={provisionPrice}
                      onChange={(e) => setProvisionPrice(e.target.value)}
                      placeholder="249"
                    />
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    per user / {p.cycle}
                    {p.listRate != null && (
                      <>
                        {" · list "}{money(p.listRate)}
                        {p.perUser != null && p.perUser !== p.listRate && (
                          <span className={p.perUser < p.listRate
                            ? "text-emerald-600 dark:text-emerald-400"
                            : "text-amber-600 dark:text-amber-400"}>
                            {" ("}
                            {p.perUser < p.listRate ? "−" : "+"}
                            {money(Math.abs(p.perUser - p.listRate))}
                            {")"}
                          </span>
                        )}
                      </>
                    )}
                  </p>
                </div>
              ) : (
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {p.customWillBeLost
                    ? "Drops the existing negotiated rate — bills at list price."
                    : "Bills at the plan's list price."}
                </p>
              )}
            </div>

            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="provision-note">Note (optional)</Label>
              <Textarea
                id="provision-note"
                rows={2}
                placeholder="e.g. invoice INV-2043, agreed on call"
                value={provisionNote}
                onChange={(e) => setProvisionNote(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter className="gap-2 sm:justify-between border-t border-slate-200 dark:border-slate-800 pt-4">
            {/* Never a dead end: if the button is off, this says why. */}
            <span className="hidden sm:flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
              {blockedReason && !provisioning && (
                <>
                  <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                  {blockedReason}
                </>
              )}
            </span>
            <div className="flex gap-2">
              <Button variant="outline" disabled={provisioning} onClick={() => setProvisionOpen(false)}>
                Cancel
              </Button>
              <Button
                // Block the cases the API would reject anyway, so the user finds
                // out before the round trip rather than via a red toast.
                disabled={provisioning || !!blockedReason}
                onClick={submitProvision}
              >
                {provisioning
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : provisionRenew
                    ? "Renew subscription"
                    : "Update subscription"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
  );
}
